import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Per-section "I've worked through this" checkboxes.
 *
 * State shape: `{ [lessonId]: { [sectionIndex]: true } }`. Sparse — only
 * checked entries are stored. Anything missing means unchecked.
 *
 * Lesson roll-up: a lesson is `complete` when every section index from
 * 0..(totalSections-1) is in the set; `partial` when some are; `none` when
 * none are.
 */

const STORAGE_KEY = 'progress';

type LessonProgress = Record<number, true>;
type AllProgress = Record<string, LessonProgress>;

export type LessonStatus = 'none' | 'partial' | 'complete';

type Ctx = {
  isChecked: (lessonId: string, sectionIndex: number) => boolean;
  toggle: (lessonId: string, sectionIndex: number) => void;
  lessonStatus: (lessonId: string, totalSections: number) => LessonStatus;
  checkedCount: (lessonId: string) => number;
  clearLesson: (lessonId: string) => void;
};

const ProgressContext = createContext<Ctx | null>(null);

function load(): AllProgress {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    // Light normalization — coerce any persisted shape to our expected one.
    const out: AllProgress = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (!v || typeof v !== 'object') continue;
      const inner: LessonProgress = {};
      for (const [i, val] of Object.entries(v)) {
        const idx = Number(i);
        if (Number.isInteger(idx) && val === true) inner[idx] = true;
      }
      if (Object.keys(inner).length > 0) out[k] = inner;
    }
    return out;
  } catch {
    return {};
  }
}

export function ProgressProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AllProgress>(load);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const isChecked = useCallback(
    (lessonId: string, sectionIndex: number) => state[lessonId]?.[sectionIndex] === true,
    [state],
  );

  const toggle = useCallback((lessonId: string, sectionIndex: number) => {
    setState((prev) => {
      const lesson = { ...(prev[lessonId] ?? {}) };
      if (lesson[sectionIndex]) {
        delete lesson[sectionIndex];
      } else {
        lesson[sectionIndex] = true;
      }
      const next = { ...prev };
      if (Object.keys(lesson).length === 0) {
        delete next[lessonId];
      } else {
        next[lessonId] = lesson;
      }
      return next;
    });
  }, []);

  const lessonStatus = useCallback(
    (lessonId: string, totalSections: number): LessonStatus => {
      const lesson = state[lessonId];
      if (!lesson) return 'none';
      const count = Object.keys(lesson).length;
      if (count === 0) return 'none';
      if (count >= totalSections) return 'complete';
      return 'partial';
    },
    [state],
  );

  const checkedCount = useCallback(
    (lessonId: string) => Object.keys(state[lessonId] ?? {}).length,
    [state],
  );

  const clearLesson = useCallback((lessonId: string) => {
    setState((prev) => {
      if (!prev[lessonId]) return prev;
      const next = { ...prev };
      delete next[lessonId];
      return next;
    });
  }, []);

  const value = useMemo<Ctx>(
    () => ({ isChecked, toggle, lessonStatus, checkedCount, clearLesson }),
    [isChecked, toggle, lessonStatus, checkedCount, clearLesson],
  );

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}

export function useProgress(): Ctx {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used within ProgressProvider');
  return ctx;
}

/** Bound to a single section. Returns the checked state + a toggle function. */
export function useSectionProgress(lessonId: string, sectionIndex: number) {
  const { isChecked, toggle } = useProgress();
  return {
    checked: isChecked(lessonId, sectionIndex),
    toggle: () => toggle(lessonId, sectionIndex),
  };
}
